import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Link, catchAll } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS, LabelL } from '@sorare/core/src/atoms/typography';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import blueprintLimited from 'assets/revealable-cards/blueprint-limited.png';
import blueprintRare from 'assets/revealable-cards/blueprint-rare.png';
import blueprintSuperRare from 'assets/revealable-cards/blueprint-super_rare.png';
import blueprintUnique from 'assets/revealable-cards/blueprint-unique.png';

import { MyCardsLayoutContext } from '../MyCardsLayout/MyCardsLayoutContext';
import type {
  RevealableCampaignQuery,
  RevealableCampaignQueryVariables,
  RevealableCardcampaign_RevealableCardCampaign,
} from './__generated__/index.graphql';
import {
  BlueprintCountByRarity_CurrentUser,
  InventoryQuery,
  InventoryQueryVariables,
} from './__generated__/index.graphql';

export const BLUEPRINT_COUNT_BY_RARITY = gql`
  fragment BlueprintCountByRarity_CurrentUser on CurrentUser {
    slug
    blueprintLimitedCards: blueprintCards(
      sport: $sport
      rarities: [limited]
      first: 1
    ) {
      totalCount
    }
    blueprintRareCards: blueprintCards(
      sport: $sport
      rarities: [rare]
      first: 1
    ) {
      totalCount
    }
    blueprintSuperRareCards: blueprintCards(
      sport: $sport
      rarities: [super_rare]
      first: 1
    ) {
      totalCount
    }
    blueprintUniqueCards: blueprintCards(
      sport: $sport
      rarities: [unique]
      first: 1
    ) {
      totalCount
    }
  }
` as TypedDocumentNode<BlueprintCountByRarity_CurrentUser>;

const CAMPAIN_COUNT = gql`
  fragment RevealableCardcampaign_RevealableCardCampaign on RevealableCardCampaign {
    slug
    name
    myLimitedBlueprintCards: myBlueprintCards(
      first: 1
      rarities: limited
      sport: $sport
    ) {
      nodes {
        slug
        pictureUrl
      }
      totalCount
    }
    myRareBlueprintCards: myBlueprintCards(
      first: 1
      rarities: rare
      sport: $sport
    ) {
      nodes {
        slug
        pictureUrl
      }
      totalCount
    }
    mySuperRareBlueprintCards: myBlueprintCards(
      first: 1
      rarities: super_rare
      sport: $sport
    ) {
      nodes {
        slug
        pictureUrl
      }
      totalCount
    }
    myUniqueBlueprintCards: myBlueprintCards(
      first: 1
      rarities: unique
      sport: $sport
    ) {
      nodes {
        slug
        pictureUrl
      }
      totalCount
    }
  }
` as TypedDocumentNode<RevealableCardcampaign_RevealableCardCampaign>;
const INVENTORY_QUERY = gql`
  query InventoryQuery($sport: Sport!) {
    config {
      id
      activeRevealableCardCampaigns {
        slug
        name
        environmentPictureUrl
        blueprintCardEditions
        myBlueprintCards(sport: $sport) {
          totalCount
        }
      }
    }
  }
` as TypedDocumentNode<InventoryQuery, InventoryQueryVariables>;

const REVEALABLE_CAMPAIGN_QUERY = gql`
  query RevealableCampaignQuery($sport: Sport!, $campaignSlug: String!) {
    revealableCardCampaign(slug: $campaignSlug) {
      slug
      ...RevealableCardcampaign_RevealableCardCampaign
    }
  }

  ${CAMPAIN_COUNT}
` as TypedDocumentNode<
  RevealableCampaignQuery,
  RevealableCampaignQueryVariables
>;

const Root = styled(Vertical)`
  height: calc(
    var(--100vh) - var(--current-stack-height, 0px) - var(
        --bottomsheet-shrinked-height,
        0px
      ) - var(--bottom-bar-height-mobile, 0px)
  );

  @media ${desktopAndAbove} {
    height: 100%;
  }
`;
const OutletContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Content = styled(Vertical)`
  background-color: var(--c-black);
  padding: var(--double-unit);
  gap: var(--unit);

  @media ${laptopAndAbove} {
    gap: var(--triple-unit);
    padding: var(--double-unit) 0;
  }
`;
const Grid = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: var(--unit);
  max-width: 100%;
  overflow-x: auto;

  @media ${desktopAndAbove} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0;
  }
`;
const StyledLink = styled(Link)`
  aspect-ratio: 1;
  & > * {
    width: 100%;
    aspect-ratio: 1;
  }
`;
const Item = styled(Horizontal).attrs({ gap: 0, center: true })`
  border: 1px solid var(--c-nd-100);
  background: radial-gradient(
    40% 40% at 50% 50%,
    #424242 0%,
    var(--c-black) 100%
  );
  border-radius: var(--unit);
  aspect-ratio: 1;
  position: relative;
  width: calc(10 * var(--unit));

  @media ${desktopAndAbove} {
    width: unset;
  }

  &:hover {
    border-color: var(--c-nd-400);
  }

  &.selected {
    border-color: var(--c-blue-400);
  }

  &.global {
    img {
      filter: grayscale(1);
    }
  }

  img {
    width: 65%;
    aspect-ratio: 1;
    object-fit: contain;

    @media ${desktopAndAbove} {
      width: calc(8 * var(--unit));
    }
  }

  span {
    font: var(--t-body-m);
    line-height: 1;
    position: absolute;
    right: var(--unit);
    bottom: var(--unit);
  }
`;

const blueprintsImagesByRarity: Record<Rarity, string | undefined> = {
  limited: blueprintLimited,
  rare: blueprintRare,
  super_rare: blueprintSuperRare,
  unique: blueprintUnique,
  common: undefined,
  custom_series: undefined,
};

type Props = {
  sport: Sport;
  type: Rarity;
};
export const MyCardsRevealableCardsLayout = ({ sport, type }: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { pathname } = useLocation();
  const { generateSportContextPath } = useSportContext();
  const { rewardsBannerHeight } = useContext(MyCardsLayoutContext);

  const { data, loading } = useQuery(INVENTORY_QUERY, {
    variables: {
      sport,
    },
  });

  const revealableCardCampaigns =
    data?.config.activeRevealableCardCampaigns ?? [];

  const cardEdition = useQueryString('cardEdition');
  const [displayedCardEdition, setDisplayedCardEdition] = useState<
    string | undefined
  >(cardEdition);

  if (
    revealableCardCampaigns &&
    displayedCardEdition === undefined &&
    revealableCardCampaigns[0]?.blueprintCardEditions[0]
  ) {
    setDisplayedCardEdition(
      revealableCardCampaigns[0]?.blueprintCardEditions[0]
    );
  }

  const selectedCampaignSlug =
    (displayedCardEdition !== undefined &&
      revealableCardCampaigns.find(c =>
        c.blueprintCardEditions.includes(displayedCardEdition)
      )?.slug) ||
    revealableCardCampaigns[0]?.slug;

  const { data: data2 } = useQuery(REVEALABLE_CAMPAIGN_QUERY, {
    variables: {
      sport,
      campaignSlug: selectedCampaignSlug!,
    },
    skip: !selectedCampaignSlug,
  });

  if (loading) {
    return <LoadingIndicator fullScreen />;
  }

  const {
    myLimitedBlueprintCards,
    myRareBlueprintCards,
    mySuperRareBlueprintCards,
    myUniqueBlueprintCards,
  } = data2?.revealableCardCampaign || {};

  const blockchainRarities = [
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ] as const;

  const blueprintsCountByRarity: Record<
    (typeof blockchainRarities)[number],
    number
  > = {
    limited: myLimitedBlueprintCards?.totalCount ?? 0,
    rare: myRareBlueprintCards?.totalCount ?? 0,
    super_rare: mySuperRareBlueprintCards?.totalCount ?? 0,
    unique: myUniqueBlueprintCards?.totalCount ?? 0,
  };

  const outletContext = {
    selectedCampaign: selectedCampaignSlug,
  };

  return (
    <ConditionalWrapper
      Wrapper={ListWithDetailLayout}
      props={{ outletContext, extraStackHeight: rewardsBannerHeight }}
      wrap={isLaptopAndAbove}
    >
      <Root>
        {!isLaptopAndAbove && (
          <OutletContainer>
            <Outlet context={outletContext} />
          </OutletContainer>
        )}
        {isLaptopAndAbove && (
          <Content>
            {revealableCardCampaigns.length > 1 && (
              <DropdownSelect
                size="medium"
                value={displayedCardEdition}
                options={revealableCardCampaigns.map(
                  ({ name, blueprintCardEditions }) => ({
                    label: <LabelL as="span">{name}</LabelL>,
                    value: blueprintCardEditions[0],
                  })
                )}
                onChange={edition => {
                  setDisplayedCardEdition(edition);
                }}
              />
            )}
            <Vertical>
              {isLaptopAndAbove && (
                <HeadlineS as="p">
                  <FormattedMessage {...fantasy.revealCards} />
                </HeadlineS>
              )}
              <Grid>
                {blockchainRarities.map(cardType => {
                  const count = blueprintsCountByRarity[cardType];

                  return (
                    <StyledLink
                      key={cardType}
                      replace
                      to={generateSportContextPath(
                        ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE,
                        {
                          params: {
                            type: cardType,
                          },
                        }
                      )}
                      aria-label={`${cardType} revealable cards`}
                    >
                      <Item
                        className={classNames({
                          selected:
                            matchAnySportPath(
                              catchAll(
                                ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE
                              ),
                              pathname
                            ) && cardType === type,
                        })}
                      >
                        <img
                          src={blueprintsImagesByRarity[cardType]}
                          alt={cardType}
                        />
                        <span>{count}</span>
                      </Item>
                    </StyledLink>
                  );
                })}
              </Grid>
            </Vertical>
          </Content>
        )}
      </Root>
    </ConditionalWrapper>
  );
};
